import React from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { CheckCircle2, Printer, Phone, Download } from "lucide-react";
import { COMPANY } from "@/data/site";

const PHASES = [
  { title: "8 Weeks Out", items: ["Inventory every room", "Declutter — donate, sell, discard", "Request 2–3 written quotes", "Set budget with 10–15% cushion"] },
  { title: "6 Weeks Out", items: ["Book your moving date", "Notify school/employer", "Start using up frozen food", "Order specialty packing materials"] },
  { title: "4 Weeks Out", items: ["Schedule utilities disconnect/connect", "File USPS change of address", "Update driver license, vehicle registration", "Confirm parking/access at both addresses"] },
  { title: "2 Weeks Out", items: ["Pack out-of-season items and decor", "Label every box (room + contents)", "Refill prescriptions", "Confirm crew size & timing with movers"] },
  { title: "1 Week Out", items: ["Pack a first-night bag per person", "Empty & defrost the freezer", "Withdraw cash for tips (optional)", "Photograph utility meters"] },
  { title: "Moving Day", items: ["Walk-through with the crew", "Keep phone, chargers, water handy", "Final empty-house walk", "Sign paperwork after items placed"] },
  { title: "First Week", items: ["Unpack kitchen first, then bedrooms", "Meet two neighbors", "Locate urgent care, grocery, gas station", "Register children in new schools"] },
];

const MovingChecklistPage = () => (
  <>
    <SEO title="Free Moving Checklist for Casper WY | Crosstown Movers"
      description="Printable 8-week moving checklist for Casper, Mills, and surrounding Wyoming areas. Phase-by-phase planning for a stress-free move."
      path="/moving-checklist" />
    <section className="py-16 md:py-20 bg-[var(--ct-primary)] text-white">
      <div className="section-container">
        <div className="label-xs text-white mb-3">Free Resource</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Moving Checklist</h1>
        <p className="mt-4 text-white/85 max-w-3xl text-lg">Print-friendly, phase-by-phase — the same checklist we walk Casper families through before every move.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="/downloads/crosstown-movers-moving-checklist.pdf"
            download="crosstown-movers-moving-checklist.pdf"
            className="btn-primary"
            data-testid="download-pdf-btn"
            aria-label="Download printable moving checklist PDF"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <button onClick={() => window.print()} className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[var(--ct-primary)]" data-testid="print-btn"><Printer className="w-4 h-4" /> Print</button>
          <a href={`tel:${COMPANY.phoneTel}`} className="btn-outline !text-white !border-white hover:!bg-white hover:!text-[var(--ct-primary)]"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
        </div>
      </div>
    </section>
    <section className="py-16 bg-white">
      <div className="section-container max-w-4xl space-y-8">
        {PHASES.map((p, i) => (
          <div key={i} className="p-7 rounded-2xl border border-[var(--ct-border)] bg-[var(--ct-bg)]">
            <div className="label-xs text-[var(--ct-primary)] mb-2">Phase {i + 1}</div>
            <h2 className="text-2xl md:text-3xl font-semibold text-[var(--ct-primary)]" style={{ fontFamily: "Outfit" }}>{p.title}</h2>
            <ul className="mt-4 space-y-2">
              {p.items.map((it, j) => (
                <li key={j} className="flex gap-3 items-start"><CheckCircle2 className="w-5 h-5 text-[var(--ct-secondary)] mt-1 flex-shrink-0" /><span>{it}</span></li>
              ))}
            </ul>
          </div>
        ))}
        <div className="p-6 rounded-2xl bg-[var(--ct-primary)] text-white text-center">
          <h3 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Need help executing the plan?</h3>
          <Link to="/#quote" className="btn-primary mt-4 inline-flex">Get a Free Quote</Link>
        </div>
      </div>
    </section>
  </>
);

export default MovingChecklistPage;
